/**
 * ProFormaStatement — the "per-batch" Pro Forma financial statement card
 * that renders inside the BusinessCalc Results tab.
 *
 * Extracted from BusinessCalc.js (~215 lines) so the parent file stays
 * manageable.  Purely presentational — takes the already-computed `outputs`
 * bag + the raw `formData` from the parent and formats them into a print-
 * ready income-statement table.
 *
 * Props:
 *   formData   full form state (needed for line-item labels + monthly-cost
 *              → per-batch derivations for OpEx)
 *   outputs    computed calc outputs bag from calculate()
 *   exportPDF  handler wired to the "Export to PDF" button
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, Download } from 'lucide-react';

const num = (v) => parseFloat(v) || 0;

const ProFormaStatement = ({ formData, outputs, exportPDF }) => {
  const batches = num(formData.batches_per_month) || 1;
  const perBatch = (monthly) => (num(monthly) / batches).toFixed(2);

  return (
    <div className="print-pro-forma">
      <Card className="border border-border rounded-2xl overflow-hidden" data-testid="pro-forma-statement">
        <CardHeader className="bg-primary/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-foreground">
                Pro Forma Financial Statement
              </CardTitle>
              <CardDescription className="mt-0.5 text-sm">
                Per-batch analysis — {formData.units_per_batch} units @ ${formData.selling_price}/unit
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border/60">
              {/* REVENUE */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Revenue
                </td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-foreground/85">Gross revenue ({formData.units_per_batch} × ${formData.selling_price})</td>
                <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${outputs.total_revenue}</td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-muted-foreground">Less: sales tax ({formData.sales_tax_rate || 0}%)</td>
                <td className="px-5 py-2.5 text-right tabular-nums text-muted-foreground">-${outputs.sales_tax_amount}</td>
              </tr>
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Net revenue</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${outputs.net_revenue_after_sales_tax}</td>
              </tr>

              {/* COGS */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Cost of goods sold (COGS)
                </td>
              </tr>
              {[
                ['Ingredients', outputs.ingredient_cost],
                ['Packaging materials', outputs.packaging_cost],
                ['Direct labor', outputs.labor_cost],
                ['Electricity (production)', outputs.electricity_cost],
                ['Consumables', outputs.consumables_cost],
                ['Waste & loss', outputs.waste_loss],
              ].map(([label, val]) => (
                <tr key={label}>
                  <td className="px-5 py-2.5 pl-9 text-foreground/85">{label}</td>
                  <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${val}</td>
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Total COGS</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${outputs.total_direct_cost}</td>
              </tr>

              {/* GROSS PROFIT */}
              <tr className="bg-emerald-50 dark:bg-emerald-900/15 border-y-2 border-emerald-500/40">
                <td className="px-5 py-3.5 font-heading font-extrabold text-foreground">Gross profit</td>
                <td className="px-5 py-3.5 text-right font-heading text-lg font-extrabold tabular-nums text-emerald-700 dark:text-emerald-300">${outputs.gross_profit}</td>
              </tr>
              <tr className="bg-emerald-50 dark:bg-emerald-900/15">
                <td className="px-5 py-2 pl-9 text-xs text-emerald-700 dark:text-emerald-300">Gross margin</td>
                <td className="px-5 py-2 text-right text-xs font-heading font-extrabold tabular-nums text-emerald-700 dark:text-emerald-300">{outputs.gross_margin_percent}%</td>
              </tr>

              {/* OPEX */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Operating expenses (OpEx)
                </td>
              </tr>
              {[
                ['Rent / facility', perBatch(formData.monthly_rent)],
                ['Utilities (non-production)', ((num(formData.monthly_utilities) + num(formData.monthly_water) + num(formData.monthly_gas)) / batches).toFixed(2)],
                ['Insurance', perBatch(formData.monthly_insurance)],
                ['Marketing & advertising', perBatch(formData.monthly_marketing)],
                ['Software & professional services', ((num(formData.monthly_software) + num(formData.monthly_professional_services)) / batches).toFixed(2)],
                ['Maintenance & supplies', ((num(formData.monthly_maintenance) + num(formData.monthly_supplies)) / batches).toFixed(2)],
                ['Other overhead', perBatch(formData.other_overhead)],
              ].map(([label, val]) => (
                <tr key={label}>
                  <td className="px-5 py-2.5 pl-9 text-foreground/85">{label}</td>
                  <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${val}</td>
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Total OpEx</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${outputs.overhead_per_batch}</td>
              </tr>

              {/* CAPEX */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Capital expenses (CapEx)
                </td>
              </tr>
              {[
                ['Equipment depreciation', outputs.machine_cost_per_batch],
                ['Loan payment (principal + interest)', outputs.loan_payment_per_batch],
                ['Other CapEx (upgrades, licenses, inventory)', outputs.other_capex_per_batch],
              ].map(([label, val]) => (
                <tr key={label}>
                  <td className="px-5 py-2.5 pl-9 text-foreground/85">{label}</td>
                  <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${val}</td>
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Total CapEx</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${outputs.capex_per_batch}</td>
              </tr>

              {/* TOTAL COST + PROFIT BEFORE TAX */}
              <tr className="bg-muted/40 border-t-2 border-border">
                <td className="px-5 py-3 font-heading font-extrabold text-foreground">Total operating costs</td>
                <td className="px-5 py-3 text-right font-heading text-lg font-extrabold tabular-nums">${outputs.total_cost_per_batch}</td>
              </tr>
              <tr className="bg-primary/10 border-y-2 border-primary/30">
                <td className="px-5 py-3 font-heading font-extrabold text-primary">Profit before income tax</td>
                <td className="px-5 py-3 text-right font-heading text-lg font-extrabold tabular-nums text-primary">${outputs.profit_before_tax}</td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-muted-foreground">Less: income tax ({formData.income_tax_rate || 0}%)</td>
                <td className="px-5 py-2.5 text-right tabular-nums text-muted-foreground">-${outputs.income_tax_amount}</td>
              </tr>

              {/* NET PROFIT — the money row */}
              <tr className="bg-navy-spotlight text-background">
                <td className="px-5 py-5">
                  <div className="font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-primary">Net profit after all costs & taxes</div>
                  <div className="text-xs text-background/60 mt-0.5">This is what actually lands in your pocket per batch.</div>
                </td>
                <td className="px-5 py-5 text-right">
                  <div className="font-heading text-3xl font-extrabold tabular-nums text-amber-brand">${outputs.net_profit}</div>
                  <div className="text-xs text-background/60 mt-0.5">{outputs.net_margin_percent}% margin</div>
                </td>
              </tr>

              {/* PER UNIT */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Per-unit economics
                </td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-foreground/85">Selling price per unit</td>
                <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${formData.selling_price}</td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-foreground/85">Cost per unit (all-in)</td>
                <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${outputs.cost_per_unit}</td>
              </tr>
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Net profit per unit</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${outputs.profit_per_unit}</td>
              </tr>

              {/* TAX BURDEN */}
              <tr className="bg-muted/40">
                <td colSpan="2" className="px-5 py-3 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-primary">
                  Tax burden summary
                </td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-foreground/85">Sales tax collected</td>
                <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${outputs.sales_tax_amount}</td>
              </tr>
              <tr>
                <td className="px-5 py-2.5 pl-9 text-foreground/85">Income tax owed</td>
                <td className="px-5 py-2.5 text-right font-heading font-semibold tabular-nums">${outputs.income_tax_amount}</td>
              </tr>
              <tr className="bg-primary/5">
                <td className="px-5 py-2.5 pl-9 font-heading font-extrabold text-foreground">Total tax impact per batch</td>
                <td className="px-5 py-2.5 text-right font-heading font-extrabold tabular-nums text-primary">${(num(outputs.sales_tax_amount) + num(outputs.income_tax_amount)).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {/* Export Buttons */}
          <div className="space-y-2 no-print p-4 border-t border-border">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-full font-semibold" onClick={() => window.print()} data-testid="pro-forma-print-btn">
                <FileText className="w-4 h-4 mr-2" />
                Print financial statement
              </Button>
              <Button variant="outline" className="flex-1 rounded-full font-semibold" onClick={exportPDF} data-testid="pro-forma-export-pdf-btn">
                <Download className="w-4 h-4 mr-2" />
                Export to PDF
              </Button>
            </div>
          </div>

          {/* Professional Footer */}
          <div className="mt-4 pt-3 border-t-2 border-slate-300 text-xs print-show">
            <div className="flex justify-between mb-3 text-slate-600">
              <span>Document Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span>Plan: {formData.plan_name || 'Untitled'}</span>
            </div>
            <div className="bg-slate-100 p-3 rounded border border-slate-300">
              <p className="text-slate-700 leading-relaxed text-center text-xs">
                <strong>Disclaimer:</strong> Educational tool only — verify all figures with a qualified professional before making business decisions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProFormaStatement;
